// Pieces shared by the cabinet Payments block and the /client/payments
// (/trp/payments) page, so both read the same way: the same summary tiles,
// the same document buttons, the same PDF download.
import React from "react";
import { FileText, Download, Info, CreditCard } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../ui/tooltip";
import { toast } from "sonner";
import api from "../../lib/api";
import { cn } from "../../lib/utils";
import { tr, currentLang } from "./i18n";

const intlLocale = () => (currentLang() === "en" ? "en-GB" : "uk-UA");

const DOC_TEXT = {
  uk: {
    invoice: "Рахунок", act: "Акт",
    loadingInvoice: "Завантаження рахунку…", loadingAct: "Завантаження акта…",
    doneInvoice: "Рахунок завантажено", doneAct: "Акт завантажено",
    failed: "Не вдалося завантажити документ",
    notReady: "Рахунок ще готує менеджер — реквізити для цього платежу не заповнені",
  },
  en: {
    invoice: "Invoice", act: "Act",
    loadingInvoice: "Downloading invoice…", loadingAct: "Downloading act…",
    doneInvoice: "Invoice downloaded", doneAct: "Act downloaded",
    failed: "Couldn't download the document",
    notReady: "Your manager is still preparing this invoice",
  },
};

// format=pdf is required — without it the backend returns DOCX, which used to
// be saved as .pdf and then "failed to open" for every client.
export const downloadClientDocument = async (paymentId, type, lang = currentLang()) => {
  const t = DOC_TEXT[lang] || DOC_TEXT.uk;
  const isInvoice = type === "invoice";
  try {
    toast.info(isInvoice ? t.loadingInvoice : t.loadingAct);
    const res = await api.get(`/sales-services/${paymentId}/${type}?format=pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${isInvoice ? t.invoice : t.act}_${paymentId.slice(0, 8)}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success(isInvoice ? t.doneInvoice : t.doneAct);
  } catch (e) {
    // 409 = the manager hasn't filled the requisites for this payment yet.
    toast.error(e?.response?.status === 409 ? t.notReady : t.failed);
  }
};

export const DocButton = ({ type, onClick, lang = "uk" }) => {
  const t = DOC_TEXT[lang] || DOC_TEXT.uk;
  const Icon = type === "act" ? Download : FileText;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="inline-flex items-center gap-1 rounded-md border border-zinc-800 px-2 py-1 text-[11px] text-zinc-400 hover:text-zinc-100 hover:border-zinc-600 whitespace-nowrap"
    >
      <Icon className="h-3 w-3" /> {type === "act" ? t.act : t.invoice}
    </button>
  );
};

const TILE_TONE = {
  neutral: "text-zinc-50",
  paid: "text-green-500",
  due: "text-amber-500",
  overdue: "text-red-500",
  muted: "text-zinc-400",
};

/** Four (or so) summary tiles: label · value (native currencies) · hint. */
export const SummaryTiles = ({ tiles }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
    {tiles.map((tile) => (
      <div
        key={tile.label}
        className={cn(
          "rounded-xl border px-3 py-2.5 min-w-0",
          tile.tone === "overdue" ? "border-red-500/30 bg-red-500/5" : "border-zinc-800 bg-zinc-950/40",
        )}
      >
        <p className="text-xs text-zinc-500">{tile.label}</p>
        <p className={cn("text-sm sm:text-base font-bold mt-0.5 break-words tabular-nums", TILE_TONE[tile.tone] || TILE_TONE.neutral)}>
          {tile.value}
        </p>
        {tile.hint && <p className="text-[11px] text-zinc-500 mt-0.5 truncate" title={tile.hint}>{tile.hint}</p>}
      </div>
    ))}
  </div>
);

/** Explanations live in tooltips, not in extra lines of text. */
export const Hint = ({ text, children, className }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        {children || (
          <button type="button" aria-label={typeof text === "string" ? text : tr("Пояснення")}
            onClick={(e) => e.stopPropagation()}
            className={cn("inline-flex text-zinc-500 hover:text-zinc-200 align-middle", className)}>
            <Info className="h-3.5 w-3.5" />
          </button>
        )}
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-relaxed whitespace-pre-line">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/** Icon-only document / card-link actions, with a tooltip each. */
export const IconAction = ({ kind, onClick, href, label, disabled }) => {
  const Icon = kind === "act" ? Download : kind === "card" ? CreditCard : FileText;
  const cls = cn(
    "inline-flex items-center justify-center h-8 w-8 rounded-lg border transition-colors",
    kind === "card"
      ? "border-orange-500/40 text-orange-500 hover:bg-orange-500/10"
      : "border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-600",
  );
  if (disabled) {
    return (
      <Hint text={label}>
        <span aria-label={label} className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-dashed border-zinc-800 text-zinc-600 cursor-not-allowed">
          <Icon className="h-4 w-4" />
        </span>
      </Hint>
    );
  }
  const node = href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={cls} aria-label={label}>
      <Icon className="h-4 w-4" />
    </a>
  ) : (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} className={cls} aria-label={label}>
      <Icon className="h-4 w-4" />
    </button>
  );
  return <Hint text={label}>{node}</Hint>;
};

// Live penalty counter: the server's accrued total + the daily rate spread
// per second since page load, so the number visibly grows (honest base).
const LivePenalty = ({ base, perDay }) => {
  const [drift, setDrift] = React.useState(0);
  React.useEffect(() => {
    if (!perDay) return undefined;
    const started = Date.now();
    const t = setInterval(() => setDrift(((Date.now() - started) / 1000) * (perDay / 86400)), 1000);
    return () => clearInterval(t);
  }, [perDay]);
  return (
    <span className="tabular-nums">
      ${(base + drift).toLocaleString(intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  );
};

/**
 * Compact penalty strip (one line) — shown whenever something is overdue.
 * rows: overdue payments with {penalty, days_overdue?/invoice_due_date}.
 * graceLeftDays: for rows still inside the grace period (no penalty yet).
 */
export const PenaltyStrip = ({ rows = [], rule, lang = currentLang() }) => {
  if (!rows.length) return null;
  const accruing = rows.filter((r) => r.penalty?.amount > 0 && !r.penalty_paid_at);
  const accrued = accruing.reduce((a, r) => a + (r.penalty.amount || 0), 0);
  const perDay = accruing.reduce((a, r) => a + ((r.penalty.base_amount || 0) * (r.penalty.rate_per_day || 0)) / 100, 0);
  const inGrace = rows.filter((r) => !(r.penalty?.amount > 0) && r.graceLeft > 0);
  const nextStart = inGrace.length ? Math.min(...inGrace.map((r) => r.graceLeft)) : null;
  const uk = lang === "uk";
  const ruleText = rule
    ? uk
      ? `Пеня — ${rule.rate_per_day}% за кожен день від суми ${rule.base === "project_total" ? "договору" : "рахунку"}, починається через ${rule.grace_period_days || 0} дн. після дати оплати в рахунку. Оплатіть — і нарахування зупиниться.`
      : `Penalty — ${rule.rate_per_day}%/day of the ${rule.base === "project_total" ? "contract" : "invoice"} amount, starting ${rule.grace_period_days || 0} days after the due date. Pay and it stops.`
    : null;
  const usd = (n) => `$${n.toLocaleString(intlLocale(), { maximumFractionDigits: 0 })}`;
  if (accrued <= 0 && nextStart == null) return null;
  return (
    <div className={cn(
      "flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border px-4 py-2.5 text-sm",
      accrued > 0 ? "border-red-500/30 bg-red-500/5" : "border-amber-500/30 bg-amber-500/5",
    )}>
      {accrued > 0 ? (
        <>
          <span className="inline-flex items-center gap-2 text-red-500 font-semibold">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
            </span>
            {uk ? "Нараховується пеня" : "Penalty accruing"}
          </span>
          <span className="text-base font-bold text-red-500"><LivePenalty base={accrued} perDay={perDay} /></span>
          {perDay > 0 && <span className="text-xs text-zinc-400">+{usd(perDay)} {uk ? "щодня" : "per day"}</span>}
          {nextStart != null && (
            <span className="text-xs text-zinc-500">
              {uk ? `ще ${inGrace.length} рах. — пеня через ${nextStart} дн.` : `${inGrace.length} more start in ${nextStart}d`}
            </span>
          )}
        </>
      ) : (
        <span className="text-amber-500 font-medium">
          {uk ? `Пеня почне нараховуватись через ${nextStart} дн. — встигніть оплатити` : `Penalty starts in ${nextStart}d — pay before then`}
        </span>
      )}
      {ruleText && <span className="ml-auto"><Hint text={ruleText} /></span>}
    </div>
  );
};

/**
 * Penalty panel — a sensitive topic, so it is spelled out in words, not
 * hidden in a tooltip: the rule, then every overdue invoice with the actual
 * arithmetic (base × rate × charged days). Goes at the top of the page.
 * rows: overdue payments {id, title, sub, amount, currency, invoice_due_date,
 *        penalty?, graceLeft}.
 */
export const PenaltyDetails = ({ rows = [], rule }) => {
  if (!rows.length || !rule) return null;
  const accruing = rows.filter((r) => r.penalty?.amount > 0 && !r.penalty_paid_at);
  const accrued = accruing.reduce((a, r) => a + (r.penalty.amount || 0), 0);
  const perDay = accruing.reduce((a, r) => a + ((r.penalty.base_amount || 0) * (r.penalty.rate_per_day || 0)) / 100, 0);
  const fmtUsd = (n, d = 2) => `$${Number(n || 0).toLocaleString(intlLocale(), { minimumFractionDigits: d, maximumFractionDigits: d })}`;
  const fmtD = (iso) => {
    if (!iso) return "—";
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso);
  };
  const addDays = (iso, n) => {
    const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const d = new Date(+m[1], +m[2] - 1, +m[3] + n);
    return d.toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit", year: "numeric" });
  };
  const baseWord = rule.base === "project_total" ? tr("суми договору") : tr("суми рахунку");
  const grace = rule.grace_period_days || 0;
  return (
    <div className="space-y-3 pt-3 border-t border-zinc-800/80">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="text-sm text-zinc-300 max-w-3xl leading-relaxed">
            {tr("За договором, якщо рахунок не оплачено до дати, вказаної в ньому, нараховується пеня —")}{" "}
            <b className="text-zinc-100">{rule.rate_per_day}% {tr("від")} {baseWord} {tr("за кожен день прострочення")}</b>.
            {grace > 0 && <> {tr("Перші")} <b className="text-zinc-100">{grace} {tr("дн.")}</b> {tr("після дати оплати — пільгові, без пені.")}</>}{" "}
            {tr("Пеня рахується в доларах США (суму рахунку перераховано за курсом) і зупиняється в день оплати.")}
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 overflow-x-auto">
        <div className="min-w-[760px] grid grid-cols-[minmax(0,1.6fr)_130px_110px_120px_minmax(0,1.3fr)_110px] gap-x-3 px-3 py-2 text-[11px] text-zinc-500 border-b border-zinc-800">
          <span>{tr("Рахунок")}</span><span className="text-right">{tr("Сума")}</span><span>{tr("Оплатити до")}</span><span>{tr("Прострочено")}</span><span>{tr("Розрахунок")}</span><span className="text-right">{tr("Пеня")}</span>
        </div>
        {rows.map((r) => {
          const p = r.penalty;
          const charged = p?.days_charged || 0;
          const late = p?.days_overdue ?? r.days_overdue ?? Math.max(0, grace - (r.graceLeft || 0));
          return (
            <div key={r.id} className="min-w-[760px] grid grid-cols-[minmax(0,1.6fr)_130px_110px_120px_minmax(0,1.3fr)_110px] gap-x-3 px-3 py-2 items-center text-sm border-b border-zinc-800/60 last:border-b-0">
              <div className="min-w-0">
                <p className="text-zinc-100 truncate" title={r.title}>{r.title}</p>
                {r.sub && <p className="text-xs text-zinc-500 truncate" title={r.sub}>{r.sub}</p>}
              </div>
              <span className="text-right tabular-nums text-zinc-200 whitespace-nowrap">
                {Number(r.amount || 0).toLocaleString(intlLocale(), { maximumFractionDigits: 2 })} {r.currency}
              </span>
              <span className="tabular-nums text-zinc-400">{fmtD(r.invoice_due_date)}</span>
              <span className="text-zinc-300">{late} {tr("дн.")}</span>
              <span className="text-xs text-zinc-400">
                {p?.amount > 0
                  ? `${fmtUsd(p.base_amount, 0)} × ${p.rate_per_day}% × ${charged} ${tr("дн.")}${grace ? ` (${late} − ${grace} ${tr("пільгових")})` : ""}`
                  : `${tr("пільговий період — пеня з")} ${addDays(r.invoice_due_date, grace + 1) || "—"}`}
              </span>
              <span className={cn("text-right tabular-nums font-semibold", p?.amount > 0 ? "text-red-500" : "text-zinc-500")}>
                {p?.amount > 0 ? fmtUsd(p.amount) : "$0"}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-zinc-500">
        {tr("Оплатіть рахунок — нарахування зупиниться. Якщо вважаєте, що пеню нараховано помилково, напишіть вашому менеджеру проєкту.")}
      </p>
    </div>
  );
};

/** Overdue cabinet payments → PenaltyPanel rows (title, project, grace left). */
export const penaltyRowsFromPayments = (payments = [], rulesByProject = {}) =>
  payments
    .filter((p) => p.state === "overdue")
    .map((p) => {
      const rule = rulesByProject[p.project_id];
      const m = String(p.invoice_due_date || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
      const due = m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
      const now = new Date(); now.setHours(0, 0, 0, 0);
      const late = due ? Math.round((now - due) / 86400000) : 0;
      return {
        ...p,
        title: `${p.base_name || p.name}${p.part_total > 1 ? ` · ${tr("платіж")} ${p.part_index} ${tr("з")} ${p.part_total}` : ""}`,
        sub: p.project_name,
        days_overdue: late,
        graceLeft: (rule?.grace_period_days || 0) - late,
      };
    })
    .sort((a, b) => (b.penalty?.amount || 0) - (a.penalty?.amount || 0));

/**
 * Penalty — compact by default (one small block: how much, how fast, or when
 * it starts). The rule and the per-invoice math open only on click: a client
 * does not need the explanation every time, only when asking "why this sum".
 */
export const PenaltyPanel = ({ rows = [], rule }) => {
  const [open, setOpen] = React.useState(false);
  if (!rows.length || !rule) return null;
  const accruing = rows.filter((r) => r.penalty?.amount > 0 && !r.penalty_paid_at);
  const accrued = accruing.reduce((a, r) => a + (r.penalty.amount || 0), 0);
  const perDay = accruing.reduce((a, r) => a + ((r.penalty.base_amount || 0) * (r.penalty.rate_per_day || 0)) / 100, 0);
  const grace = rule.grace_period_days || 0;
  const inGrace = rows.filter((r) => !(r.penalty?.amount > 0));
  const startDates = inGrace
    .map((r) => {
      const m = String(r.invoice_due_date || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? new Date(+m[1], +m[2] - 1, +m[3] + grace + 1) : null;
    })
    .filter(Boolean)
    .sort((a, b) => a - b);
  const nextStart = startDates[0]?.toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit", year: "numeric" });
  const usd0 = (n) => `$${Number(n || 0).toLocaleString(intlLocale(), { maximumFractionDigits: 0 })}`;
  const hot = accrued > 0;
  return (
    <section data-testid="client-penalty-panel"
      className={cn("rounded-2xl border px-4 py-3", hot ? "border-red-500/30 bg-red-500/5" : "border-amber-500/30 bg-amber-500/5")}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className={cn("inline-flex items-center gap-2 text-sm font-semibold", hot ? "text-red-500" : "text-amber-500")}>
          <span className={cn("w-2 h-2 rounded-full", hot ? "bg-red-500" : "bg-amber-500")} />
          {tr("Пеня")}
        </span>
        {hot ? (
          <>
            <span className="text-lg font-bold text-red-500"><LivePenalty base={accrued} perDay={perDay} /></span>
            {perDay > 0 && <span className="text-xs text-zinc-400">+{usd0(perDay)} {tr("щодня")}</span>}
          </>
        ) : (
          <span className="text-sm text-zinc-300">
            {nextStart ? `${tr("почнеться з")} ${nextStart}` : tr("ще не нараховується")}
            <span className="text-zinc-500"> · {rows.length} {tr(rows.length === 1 ? "рахунок" : "рахунки")} {tr("прострочено")}</span>
          </span>
        )}
        <button type="button" onClick={() => setOpen((v) => !v)}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-800/60 px-3 py-1.5 text-xs font-semibold text-zinc-100 hover:border-orange-500 hover:text-orange-500 transition-colors">
          {open ? tr("Згорнути") : tr("Чому така сума")}
          <span className={cn("transition-transform", open && "rotate-180")}>▾</span>
        </button>
      </div>
      {open && <div className="mt-3"><PenaltyDetails rows={rows} rule={rule} /></div>}
    </section>
  );
};

/**
 * Due date as numbers, not a sentence: date (mono) + a coloured day chip
 * (−N red = overdue, N amber = days left, ✓ green = done, plan grey), plus a
 * tiny grace-period bar when a penalty is about to start. Full wording sits
 * in the tooltip.
 *   kind: "overdue" | "due" | "done" | "planned"
 */
export const DueChip = ({ kind, date, label, tip, grace }) => {
  const d = (() => {
    const m = String(date || "").match(/^(\d{4})-(\d{2})-(\d{2})/) || String(date || "").match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/);
    if (!m) return null;
    return m[0].includes("-") ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(+m[3], +m[2] - 1, +m[1]);
  })();
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const diff = d ? Math.round((d - now) / 864e5) : null;
  const shown = label || (d ? d.toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
  const chip =
    kind === "done" ? { t: "✓", c: "bg-green-500/15 text-green-500 border-green-500/30" }
    : kind === "overdue" ? { t: diff != null ? `−${Math.abs(diff)} ${tr("дн.")}` : "!", c: "bg-red-500/15 text-red-500 border-red-500/30" }
    : kind === "due" ? { t: diff == null ? "…" : diff === 0 ? tr("сьогодні") : `${diff} ${tr("дн.")}`, c: "bg-amber-500/15 text-amber-500 border-amber-500/30" }
    : { t: tr("план"), c: "bg-zinc-800 text-zinc-400 border-zinc-700" };
  const body = (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className={cn("font-mono text-xs tabular-nums", kind === "planned" ? "text-zinc-500" : "text-zinc-300")}>{shown}</span>
      <span className={cn("inline-flex items-center rounded-md border px-1.5 py-px text-[11px] font-semibold tabular-nums", chip.c)}>{chip.t}</span>
      {grace && grace.total > 0 && (
        <span className="inline-flex gap-0.5" aria-label={tr("Пільговий період")}>
          {Array.from({ length: grace.total }).map((_, i) => (
            <span key={i} className={cn("h-2.5 w-1.5 rounded-sm", i < grace.used ? "bg-amber-500" : "bg-zinc-700")} />
          ))}
        </span>
      )}
    </span>
  );
  return tip ? <Hint text={tip}>{body}</Hint> : body;
};

// "ТОО «Национальный … центр «DARU»»" → "DARU"; "ФОП Дубко Андрій" → "Дубко А.";
// "SOBKO MYKHAILO (Individual Entrepreneur)" → "Sobko M."; "SmartOrange LLC (US)" → "SmartOrange".
export const shortParty = (name) => {
  if (!name) return "—";
  const quoted = [...String(name).matchAll(/[«"“]([^«»"“”]+)[»"”]/g)].map((m) => m[1].trim()).filter(Boolean);
  if (quoted.length) return quoted[quoted.length - 1];
  // JS \b is ASCII-only, so Cyrillic prefixes need explicit boundaries.
  const B = "(^|[\\s(«\"])";
  const isPerson = new RegExp(`${B}(ФОП|ФЛП|FOP)(?=$|[\\s)])`, "i").test(name) || /Individual Entrepreneur/i.test(name);
  const clean = String(name)
    .replace(/\(.*?\)/g, " ")
    .replace(new RegExp(`${B}(ТОВ|ТОО|ТзОВ|ПП|ПрАТ|ФОП|ФЛП|LLC|Inc\\.?|Ltd\\.?|GmbH|FOP)(?=$|[\\s)])`, "gi"), " ")
    .replace(/\s+/g, " ").trim();
  const words = clean.split(" ").filter(Boolean);
  const cap = (w) => (w === w.toUpperCase() ? w.charAt(0) + w.slice(1).toLowerCase() : w);
  if (isPerson && words.length >= 2) return `${cap(words[0])} ${words[1].charAt(0).toUpperCase()}.`;
  // Company: up to two meaningful words (no "The", stop at numbers/addresses).
  const ws = words.filter((w, i) => !(i === 0 && /^the$/i.test(w)));
  const picked = [];
  for (const w of ws) {
    if (/\d/.test(w) || picked.length === 2) break;
    if (picked.length && (picked.join(" ") + " " + w).length > 18) break;
    picked.push(cap(w));
  }
  const out = picked.join(" ") || cap(words[0] || name);
  return out.length > 18 ? `${out.slice(0, 17)}…` : out;
};

/** Who pays whom, as a tiny diagram: [client] → [our entity]; full names in the tooltip. */
export const PartiesChip = ({ payer, payee }) => {
  const ini = (s) => (shortParty(s) || "?").replace(/[^A-Za-zА-Яа-яІіЇїЄєҐґ ]/g, "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  return (
    <Hint text={`${tr("Платник")}: ${payer || "—"}\n${tr("Отримувач (наша юрособа)")}: ${payee || tr("не вказано")}`}>
      <span className="inline-flex items-center gap-1.5 max-w-full whitespace-nowrap text-xs">
        <span className="inline-flex items-center gap-1 min-w-0 rounded-full border border-zinc-700 bg-zinc-800/60 py-0.5 pl-0.5 pr-2">
          <span className="grid h-4 w-4 flex-shrink-0 place-items-center rounded-full bg-zinc-700 text-[8px] font-bold text-zinc-200">{ini(payer)}</span>
          <span className="truncate text-zinc-300">{shortParty(payer)}</span>
        </span>
        <svg viewBox="0 0 24 12" className="h-2.5 w-5 flex-shrink-0 text-zinc-500" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M1 6h20M16 1l5 5-5 5" />
        </svg>
        <span className="inline-flex items-center gap-1 min-w-0 rounded-full border border-orange-500/40 bg-orange-500/10 py-0.5 pl-0.5 pr-2">
          <span className="grid h-4 w-4 flex-shrink-0 place-items-center rounded-full bg-orange-500 text-[8px] font-bold text-black">{ini(payee)}</span>
          <span className="truncate text-orange-500">{shortParty(payee)}</span>
        </span>
      </span>
    </Hint>
  );
};
